import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { headcountApi, HeadcountPlan } from '@/lib/headcount';
import { formatMoney } from '@/lib/currency';
import { isPlanGateError } from '@/lib/planGate';
import { UpgradeWall } from '@/components/app/UpgradeWall';
import { AppShell } from '@/components/app/AppShell';
import { ConfirmModal } from '@/components/ConfirmModal';
import { CurrencyPicker } from '@/components/CurrencyPicker';
import { EntityPicker } from '@/components/EntityPicker';

export function WorkforcePage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['headcount-plans', wsId],
    queryFn: () => headcountApi.list(wsId!),
    enabled: !!wsId,
  });

  const today = new Date();
  const firstOfPrev = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().slice(0, 10);
  const lastOfNextFive = new Date(today.getFullYear(), today.getMonth() + 6, 0).toISOString().slice(0, 10);

  const [period, setPeriod] = useState({ start: firstOfPrev, end: lastOfNextFive });

  const rollout = useMutation({
    mutationFn: () => headcountApi.rollout(wsId!, { periodStart: period.start, periodEnd: period.end }),
  });

  // Part 15: project planned people-cost into fp_facts (scenario plan:headcount) for plan-vs-actual.
  const project = useMutation({
    mutationFn: () => headcountApi.project(wsId!, { periodStart: period.start, periodEnd: period.end }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scenarios', wsId] });
      qc.invalidateQueries({ queryKey: ['variances', wsId] });
    },
  });

  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<{ role: string; fte: string; salary: string; salaryLow: string; salaryHigh: string; currency: string; onCosts: string; start: string; end: string; department: string; location: string; entityId: string; notes: string }>({
    role: '', fte: '1', salary: '', salaryLow: '', salaryHigh: '', currency: 'USD', onCosts: '30', start: firstOfPrev, end: '', department: '', location: '', entityId: '', notes: '',
  });

  const create = useMutation({
    mutationFn: () => headcountApi.create(wsId!, {
      role: draft.role.trim(),
      fte: Number(draft.fte),
      salaryBandMid: Number(draft.salary),
      salaryBandLow: draft.salaryLow.trim() ? Number(draft.salaryLow) : null,
      salaryBandHigh: draft.salaryHigh.trim() ? Number(draft.salaryHigh) : null,
      department: draft.department.trim() || null,
      location: draft.location.trim() || null,
      entityId: draft.entityId || null,
      notes: draft.notes.trim() || null,
      currency: draft.currency.toUpperCase(),
      onCostsPct: Number(draft.onCosts) / 100,
      startDate: draft.start,
      endDate: draft.end || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['headcount-plans', wsId] });
      setShowForm(false);
      setDraft((d) => ({ ...d, role: '', salary: '', salaryLow: '', salaryHigh: '', department: '', location: '', entityId: '', notes: '' }));
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => headcountApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['headcount-plans', wsId] }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof headcountApi.update>[1] }) =>
      headcountApi.update(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['headcount-plans', wsId] }),
  });

  // Pending delete handled via ConfirmModal at the bottom of the page.
  const [pendingDelete, setPendingDelete] = useState<HeadcountPlan | null>(null);

  return (
    <AppShell title={t('workforce.title')} subtitle={t('workforce.subtitle')}>
      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('workforce.title')}</h1>
            <p className="mt-1 text-muted-foreground max-w-2xl">{t('workforce.subtitle')}</p>
          </div>
          <button
            onClick={() => setShowForm((s) => !s)}
            className="h-9 px-3.5 rounded-md bg-foreground text-background text-[12.5px] font-semibold hover:bg-foreground/90"
          >
            {t('workforce.new_plan')}
          </button>
        </div>

        {showForm && (
          <div className="rounded-md border border-border bg-card p-5 space-y-3">
            {/*
             * Responsive grid: 2 cols on phones, 4 cols on most laptops
             * (= 2 visual rows of 4+3 fields), 7 cols only on very wide
             * screens (2xl+). Previous sm:grid-cols-7 forced single-line
             * even on standard laptops which jammed labels into 2 lines.
             */}
            <div className="grid grid-cols-2 md:grid-cols-4 2xl:grid-cols-7 gap-2">
              <Field label={t('workforce.role')}>
                <input value={draft.role} onChange={(e) => setDraft((d) => ({ ...d, role: e.target.value }))}
                  className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
              </Field>
              <Field label={t('workforce.fte')}>
                <input type="text" inputMode="decimal" value={draft.fte} onChange={(e) => setDraft((d) => ({ ...d, fte: e.target.value }))}
                  className="num w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
              </Field>
              <Field label={t('workforce.salary_mid')}>
                <input type="text" inputMode="decimal" value={draft.salary} onChange={(e) => setDraft((d) => ({ ...d, salary: e.target.value }))}
                  placeholder="120000"
                  className="num w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
              </Field>
              <Field label={t('entities.currency')}>
                <CurrencyPicker
                  value={draft.currency}
                  onChange={(v) => setDraft((d) => ({ ...d, currency: v }))}
                />
              </Field>
              <Field label={t('workforce.on_costs')}>
                <input type="text" inputMode="decimal" value={draft.onCosts} onChange={(e) => setDraft((d) => ({ ...d, onCosts: e.target.value }))}
                  className="num w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
              </Field>
              <Field label={t('workforce.start')}>
                <input type="date" value={draft.start} onChange={(e) => setDraft((d) => ({ ...d, start: e.target.value }))}
                  className="num w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
              </Field>
              <Field label={t('workforce.end')}>
                <input type="date" value={draft.end} onChange={(e) => setDraft((d) => ({ ...d, end: e.target.value }))}
                  className="num w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
              </Field>
              <Field label={t('workforce.salary_low', { defaultValue: 'Band low' })}>
                <input type="text" inputMode="decimal" value={draft.salaryLow} onChange={(e) => setDraft((d) => ({ ...d, salaryLow: e.target.value }))}
                  className="num w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
              </Field>
              <Field label={t('workforce.salary_high', { defaultValue: 'Band high' })}>
                <input type="text" inputMode="decimal" value={draft.salaryHigh} onChange={(e) => setDraft((d) => ({ ...d, salaryHigh: e.target.value }))}
                  className="num w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
              </Field>
              <Field label={t('workforce.department', { defaultValue: 'Department' })}>
                <input value={draft.department} onChange={(e) => setDraft((d) => ({ ...d, department: e.target.value }))}
                  className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
              </Field>
              <Field label={t('workforce.location', { defaultValue: 'Location' })}>
                <input value={draft.location} onChange={(e) => setDraft((d) => ({ ...d, location: e.target.value }))}
                  className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
              </Field>
              <Field label={t('workforce.entity', { defaultValue: 'Entity' })}>
                <EntityPicker wsId={wsId!} value={draft.entityId} onChange={(id) => setDraft((d) => ({ ...d, entityId: id }))}
                  includeNone noneLabel={t('workforce.entity_none', { defaultValue: '— none —' })}
                  className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
              </Field>
              <Field label={t('workforce.notes', { defaultValue: 'Notes' })}>
                <input value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                  className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
              </Field>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="text-xs text-muted-foreground hover:text-foreground px-2 py-1">
                {t('common.cancel')}
              </button>
              <button
                onClick={() => create.mutate()}
                disabled={create.isPending || !draft.role.trim() || !draft.salary || draft.currency.length !== 3}
                className="text-xs font-semibold px-3 py-1.5 rounded-md bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60"
              >
                {t('workforce.new_plan')}
              </button>
            </div>
            {create.error && <p className="text-xs text-rose-700">{(create.error as Error).message}</p>}
          </div>
        )}

        {/* Plans table */}
        {isLoading ? (
          <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
        ) : plans.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-card p-12 text-center">
            <p className="text-foreground font-medium">{t('workforce.empty')}</p>
          </div>
        ) : (
          <div className="rounded-md border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border bg-foreground/[0.02]">
                  <th className="py-2 px-4 text-[10px] uppercase tracking-[0.16em] font-medium">{t('workforce.role')}</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-[0.16em] font-medium text-right">{t('workforce.fte')}</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-[0.16em] font-medium text-right">{t('workforce.salary_mid')}</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-[0.16em] font-medium text-right">{t('workforce.on_costs')}</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-[0.16em] font-medium">{t('workforce.start')}</th>
                  <th className="py-2 px-3 text-[10px] uppercase tracking-[0.16em] font-medium">{t('workforce.end')}</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {plans.map((p) => (
                  <PlanRow
                    key={p.id}
                    p={p}
                    onDelete={() => setPendingDelete(p)}
                    onSave={(patch) => updateMut.mutateAsync({ id: p.id, patch })}
                    t={t}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Rollout panel */}
        <div className="rounded-md border border-border bg-card p-5 space-y-3">
          <h3 className="text-sm font-semibold text-foreground">{t('workforce.rollout_title')}</h3>
          <div className="flex items-end gap-2 flex-wrap">
            <Field label={t('forecast.hist_start')}>
              <input type="date" value={period.start} onChange={(e) => setPeriod((p) => ({ ...p, start: e.target.value }))}
                className="num rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
            </Field>
            <Field label={t('forecast.hist_end')}>
              <input type="date" value={period.end} onChange={(e) => setPeriod((p) => ({ ...p, end: e.target.value }))}
                className="num rounded-md border border-border bg-white px-2 py-1.5 text-xs" />
            </Field>
            <button onClick={() => rollout.mutate()} disabled={rollout.isPending}
              className="h-[28px] px-3 rounded-md bg-foreground text-background text-[11px] font-semibold hover:bg-foreground/90 disabled:opacity-60">
              {rollout.isPending ? t('common.loading') : t('workforce.rollout_run')}
            </button>
            <button onClick={() => project.mutate()} disabled={project.isPending}
              className="h-[28px] px-3 rounded-md border border-accent-500 text-accent-700 text-[11px] font-semibold hover:bg-accent-500/10 disabled:opacity-60">
              {project.isPending ? t('common.loading') : t('workforce.project_run', { defaultValue: 'Project to plan' })}
            </button>
          </div>

          {project.error && isPlanGateError(project.error) && wsId && (
            <UpgradeWall wsId={wsId} requiredTier="growth" featureName={t('workforce.project_feature', { defaultValue: 'Workforce planning' })} />
          )}
          {project.error && !isPlanGateError(project.error) && (
            <p className="text-[12px] text-warn-600">{(project.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? t('common.error', { defaultValue: 'Something went wrong' })}</p>
          )}
          {project.data && (
            <p className="text-[12px] text-emerald-700">
              {t('workforce.project_done', {
                defaultValue: `Projected ${project.data.factsInserted} planned people-cost facts (scenario ${project.data.scenario}). Compare plan vs actual in Planning / P&L.`,
                count: project.data.factsInserted,
                scenario: project.data.scenario,
              })}
            </p>
          )}

          {rollout.data && (
            <div className="space-y-3 pt-2 border-t border-border">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {rollout.data.totalByCurrency.map((c) => (
                  <div key={c.currency} className="rounded-md border border-border bg-foreground/[0.02] p-3">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium">{c.currency}</div>
                    <div className="num text-xl font-semibold text-foreground tabular-nums mt-1">
                      {formatMoney(c.total, { currency: c.currency, maximumFractionDigits: 0 })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[360px]">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-2 pr-3 font-medium text-[10px] uppercase tracking-[0.16em]">{t('workforce.th_month')}</th>
                      <th className="py-2 px-3 font-medium text-[10px] uppercase tracking-[0.16em] text-right">{t('workforce.th_total')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rollout.data.totalByMonth.map((m) => (
                      <tr key={m.month} className="hover:bg-foreground/[0.02]">
                        <td className="py-2 pr-3 num text-foreground/90">{m.month}</td>
                        <td className="py-2 px-3 num text-right tabular-nums text-foreground">
                          {m.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      <ConfirmModal
        open={pendingDelete !== null}
        title={t('workbook.delete_confirm')}
        body={pendingDelete?.role}
        confirmLabel={t('common.delete')}
        destructive
        busy={del.isPending}
        onConfirm={() => {
          if (pendingDelete) {
            del.mutate(pendingDelete.id, { onSettled: () => setPendingDelete(null) });
          }
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </AppShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-1">{label}</span>
      {children}
    </label>
  );
}

function PlanRow({
  p, onDelete, onSave, t,
}: {
  p: HeadcountPlan;
  onDelete: () => void;
  onSave: (patch: Parameters<typeof headcountApi.update>[1]) => Promise<unknown>;
  t: (k: string) => string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    role: p.role,
    fte: String(p.fte),
    salary: String(p.salaryBandMid),
    currency: p.currency,
    onCosts: String(Math.round(p.onCostsPct * 100)),
    start: p.startDate,
    end: p.endDate ?? '',
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await onSave({
        role: draft.role.trim(),
        fte: Number(draft.fte),
        salaryBandMid: Number(draft.salary),
        currency: draft.currency.toUpperCase(),
        onCostsPct: Number(draft.onCosts) / 100,
        startDate: draft.start,
        endDate: draft.end || null,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    const inputCls = 'w-full rounded-md border border-border bg-white px-2 py-1 text-xs';
    return (
      <tr className="bg-foreground/[0.03]">
        <td className="py-2 px-4"><input value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} className={inputCls} /></td>
        <td className="py-2 px-3"><input value={draft.fte} onChange={(e) => setDraft({ ...draft, fte: e.target.value })} className={`num ${inputCls} text-right`} inputMode="decimal" /></td>
        <td className="py-2 px-3"><input value={draft.salary} onChange={(e) => setDraft({ ...draft, salary: e.target.value })} className={`num ${inputCls} text-right`} inputMode="decimal" /></td>
        <td className="py-2 px-3"><input value={draft.onCosts} onChange={(e) => setDraft({ ...draft, onCosts: e.target.value })} className={`num ${inputCls} text-right`} inputMode="decimal" /></td>
        <td className="py-2 px-3"><input type="date" value={draft.start} onChange={(e) => setDraft({ ...draft, start: e.target.value })} className={`num ${inputCls}`} /></td>
        <td className="py-2 px-3"><input type="date" value={draft.end} onChange={(e) => setDraft({ ...draft, end: e.target.value })} className={`num ${inputCls}`} /></td>
        <td className="py-2 px-3 text-right whitespace-nowrap">
          <button onClick={() => setEditing(false)} disabled={saving} className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-1">
            {t('common.cancel')}
          </button>
          <button onClick={save} disabled={saving} className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 px-2 py-1 disabled:opacity-60">
            {saving ? t('settings.saving') : t('common.save') ?? t('variance.save_note')}
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="hover:bg-foreground/[0.02]">
      <td className="py-2 px-4 font-medium text-foreground">{p.role}</td>
      <td className="num py-2 px-3 text-right tabular-nums">{p.fte.toFixed(1)}</td>
      <td className="num py-2 px-3 text-right tabular-nums">
        {p.salaryBandMid.toLocaleString(undefined, { maximumFractionDigits: 0 })} {p.currency}
      </td>
      <td className="num py-2 px-3 text-right tabular-nums">{(p.onCostsPct * 100).toFixed(0)}%</td>
      <td className="num py-2 px-3 text-foreground/80">{p.startDate}</td>
      <td className="num py-2 px-3 text-foreground/80">{p.endDate ?? '—'}</td>
      <td className="py-2 px-3 text-right whitespace-nowrap">
        <button onClick={() => setEditing(true)} className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-1">
          {t('common.edit') ?? 'Edit'}
        </button>
        <button onClick={onDelete} className="text-[11px] text-muted-foreground hover:text-rose-700 px-2 py-1">
          {t('workbook.delete')}
        </button>
      </td>
    </tr>
  );
}
